import { queryOptions, useQueries, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'

import { intentionApi } from '../api/intentionApi'
import type { TGetIntentionsPayload } from '../api/types/getIntentionsApi.types'
import { INTENTION_TYPE } from '../models/Intention.enums'
import type { TIntention } from '../models/Intention.types'
import { getIntentionQueryOptions } from './useIntentionQuery'

export const getIntentionsQueryOptions = (payload: TGetIntentionsPayload, queryClient: QueryClient | null) =>
  queryOptions({
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['intentions', payload?.landingPageSlug],
    queryFn: async ({ signal }) => {
      const intentions = await intentionApi.getIntentions(payload, { signal })

      if (queryClient) {
        // https://tkdodo.eu/blog/seeding-the-query-cache#push-approach
        // The pull approach is not suitable because we don't know the getIntentions payload in getIntention options
        intentions.forEach((intention) => {
          queryClient.setQueryData(
            getIntentionQueryOptions<INTENTION_TYPE.INTENTION>(intention.id, undefined, INTENTION_TYPE.INTENTION)
              .queryKey,
            intention,
          )
        })
      }

      // Normalize the data so that there is only one source of truth #normalizeIntentions
      return intentions.map(({ id }) => id)
    },
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: 60 * 1000 * 60, // 1 hour
    gcTime: 60 * 1000 * 60, // 1 hour
  })

export const useIntentionsQuery = (payload: TGetIntentionsPayload) => {
  const queryClient = useQueryClient()

  const query = useQuery(getIntentionsQueryOptions(payload, queryClient))

  const intentions = useQueries({
    queries: query.data
      ? query.data.map((id) => {
          const intention = queryClient.getQueryData(
            getIntentionQueryOptions(id, undefined, INTENTION_TYPE.INTENTION).queryKey,
          )
          return getIntentionQueryOptions(id, intention?.shareToken, INTENTION_TYPE.INTENTION)
        })
      : [],
    combine: (results) =>
      results.map((result) => result.data).filter((intention): intention is TIntention => !!intention),
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    intentions: query.data && intentions,
    ids: query.data,
  }
}
